import React, { useEffect } from "react";
import { Route, Routes, useParams } from "react-router-dom";
import { useSnapshot } from "valtio";
import { fetchGameSummary, fetchTimeline } from "./api";
import { getStrategy } from "./api/strategies";
import { DesktopLayout } from "./DesktopLayout";
import { useIsMobile, useQuery } from "./hooks";
import { MobileLayout } from "./Mobile/MobileLayout";
import "./index.css";
import { AuthModal } from "./modals/Auth/AuthModal";
import { LoginForm } from "./modals/Auth/LoginForm";
import { RegisterForm } from "./modals/Auth/RegisterForm";
import { RequestResetForm } from "./modals/Auth/RequestResetForm";
import { Modals } from "./modals/Modals";
import { useNetwork } from "./network/hooks";
import { updateAuthUser } from "./states/authState";
import { loadMapStateFromGame, loadMapStateFromStrategy, playbookState } from "./states/frameState";
import { closeAllModals, matchState, platformState, uiState } from "./states/uiStates";
import { RegionV5 } from "./types/riot";
import { useApi } from "@shared/api/hooks";
import { normalizeRegionToV5 } from "@shared/utils";
import { fetchCustomGame, fetchCustomGameByToken } from "@shared/api/custom_game";
import { ResetPasswordForm } from "@shared/modals/Auth/ResetPasswordForm";
import { Spinner } from "@shared/components/Spinner";

let pendingFrameIndex: number | undefined;

export const Main = () => {
	const playbookSnap = useSnapshot(playbookState);
	const matchSnap = useSnapshot(matchState);
	const platformSnap = useSnapshot(platformState);
	const { strategyId } = useParams();
	const query = useQuery();
	const isMobile = useIsMobile();

	const { request: requestTimeline } = useApi(fetchTimeline);
	const { request: requestSummary } = useApi(fetchGameSummary);
	const { request: requestCustomGameByToken } = useApi(fetchCustomGameByToken);
	const { request: requestCustomGame } = useApi(fetchCustomGame);

	useEffect(() => {
		async function loadTimeline(region: RegionV5, gameId: string) {
			const result = await requestTimeline(region, gameId);
			return result?.timeline;
		}

		async function loadSummary(region: RegionV5, gameId: string) {
			const result = await requestSummary(region, gameId);
			return result?.summary;
		}

		async function loadGame() {
			const { region, gameId } = playbookSnap.game!;
			matchState.loading = true;
			try {
				const [summary, timeline] = await Promise.all([
					loadSummary(normalizeRegionToV5(region), gameId),
					loadTimeline(normalizeRegionToV5(region), gameId),
				]);
				if (!!summary && !!timeline) {
					uiState.currentFrameIndex = pendingFrameIndex ?? 0;
					pendingFrameIndex = undefined;
					matchState.gameProvider = "api";
					matchState.match = { summary, timeline };
				}
			} finally {
				matchState.loading = false;
			}
		}

		async function loadCustomGame() {
			if (playbookSnap.customGameId && playbookSnap.customGameId === matchSnap.customGameId) {
				return;
			}
			matchState.loading = true;
			try {
				const customGame = await requestCustomGameByToken(playbookSnap.customMatchToken!);
				if (customGame.match && customGame.custom_game.game_provider) {
					uiState.currentFrameIndex = 0;
					matchState.gameProvider = customGame.custom_game.game_provider;
					matchState.match = customGame.match;
				}
			} finally {
				matchState.loading = false;
			}
		}

		if (!playbookSnap.game && !playbookSnap.customMatchToken && !playbookSnap.customGameId) {
			matchState.match = undefined;
			matchState.gameProvider = undefined;
			matchState.customGameId = undefined;
		} else {
			if (playbookSnap.game) {
				loadGame();
			} else if (playbookSnap.customMatchToken && playbookSnap.customGameId) {
				loadCustomGame();
			}
		}
	}, [playbookSnap.game, playbookSnap.customMatchToken, matchSnap.customGameId]);

	useEffect(() => {
		(async () => {
			if (!strategyId) return;

			try {
				const res = await getStrategy(strategyId);
				// TODO f2 not found?
				if (!res || !res.ok) return;

				if (res.strategy.custom_game_id) {
					const custom_game_res = await requestCustomGame(res.strategy.custom_game_id);
					playbookState.customMatchToken = custom_game_res.token;
					loadMapStateFromStrategy(
						res.strategy,
						custom_game_res.custom_game,
						custom_game_res.match,
					);
				} else {
					loadMapStateFromStrategy(res.strategy);
				}

				closeAllModals();
			} catch (e) {
				// TODO f2 error handling
			}
		})();
	}, [strategyId]);

	useEffect(() => {
		const regionParam = query.get("region") as RegionV5 | null;
		const matchIdParam = query.get("matchId");
		const frameParam = query.get("frame");

		if (regionParam && matchIdParam && !strategyId) {
			loadMapStateFromGame(regionParam, matchIdParam);
		}

		if (frameParam) {
			const frameIndex = parseInt(frameParam, 10);
			if (!isNaN(frameIndex)) {
				pendingFrameIndex = frameIndex;
			}
		}

		updateAuthUser();
	}, []);

	useNetwork();

	const useMobileLayout = isMobile && platformSnap.platform === "web";

	return (
		<>
			{useMobileLayout ? <MobileLayout /> : <DesktopLayout />}
			<Routes>
				<Route
					path={"/login"}
					element={
						<AuthModal title={"Login"}>
							<LoginForm />
						</AuthModal>
					}
				/>
				<Route
					path={"/request-reset"}
					element={
						<AuthModal title={"Reset Password"}>
							<RequestResetForm />
						</AuthModal>
					}
				/>
				<Route
					path={"/reset-password"}
					element={
						<AuthModal title={"Reset Password"}>
							<ResetPasswordForm />
						</AuthModal>
					}
				/>
				<Route
					path={"/register"}
					element={
						<AuthModal title={"Register"}>
							<RegisterForm />
						</AuthModal>
					}
				/>
			</Routes>
			<Modals />
			{matchSnap.loading && (
				<div
					className={
						"fixed inset-0 z-[9999] flex items-center justify-center bg-black/40"
					}
				>
				<Spinner className={"w-12 h-12"} />
			</div>
			)}
		</>
	);
};
